/**
 * External dependencies
 */
import React, { useState, useEffect } from "react";
import ServiceCard from "../../service-card/component";

/**
 * Internal dependencies
 */
import {
	useUrlParamState,
	useFilters,
	usePosts,
	useTermsAsSelectOptions,
} from "./hooks";
import { FiltersLoader, ResultsLoader, Spinner } from "./loaders";
import {
	ErrorMessage,
	Select,
	ButtonSelect,
} from "./components";
import { Post } from "./api/types";

/**
 * Constants
 */
const SEARCH_DEBOUNCE_DURATION = 1000;

/**
 * Application
 */
interface AppProps {
	perPage: number;
	texts: Record<string, string>;
}

const App = ({ perPage, texts }: AppProps) => {

	// Add media query for switching between ButtonSelect and MultiSelect for filter menu
	const [matches, setMatches] = useState(
		window.matchMedia("(min-width: 782px)").matches
	)
	useEffect(() => {
		window
		.matchMedia("(min-width: 782px)")
		.addEventListener('change', e => setMatches( e.matches ));
	}, []);

	/**
	 * Load filter data
	 */
	const { data, error } = useFilters();
	const { service_categories } = data || {};
	const serviceCatFilterOpts = useTermsAsSelectOptions(service_categories);


	/**
	 * Setup filter state
	 */
	const [selectedServiceCategories, setSelectedServiceCategories] = useUrlParamState(
		"ytk_service_category",
		[] as (number | string)[]
	);

	/**
	 * Build query params for Posts API
	 */
	const queryParams = {
		service_categories: selectedServiceCategories || [],
	};

	/**
	 * Error and loading state
	 */
	if (error) {
		return (
			<ErrorMessage
				title={texts?.filterErrorTitle}
				text={texts?.filterErrorText}
			/>
		);
	}

	if (!data) {
		return (
			<div className="em-block-service-feed__app">
				<FiltersLoader numOfItems={2} />
				<ResultsLoader numOfItems={perPage} />
			</div>
		);
	}

	// Get the translation string for Show all filter button
	// @ts-ignore
	const translate = window.emYtkBlocksConfig;

	return (
		<div className="em-block-service-feed__app">
			<div className="em-block-service-feed__filters">
			{/* <div className="em-block-service-feed__filters" aria-label={translate?.text?.StoryFilteringMenu ?? "Service filtering menu"}> */}
				{/* Switch between two different filter menus based on mobile/desktop screen */}
				{matches ? (
					<ButtonSelect
						label={texts?.serviceCategoryPlaceholder || ""}
						selected={selectedServiceCategories}
						setSelected={setSelectedServiceCategories}
						options={serviceCatFilterOpts}
						orientation={"horizontal"}
						showAllText={translate?.text?.showAll ?? "Show all"}
					/>
				) : (
					<>
						{/* <label className="screen-reader-text" id="aria-label-for-category-input" htmlFor="service-filter-category-input">
							{texts?.serviceCategoryLabel || ""}
						</label> */}

						<Select
							inputId="service-filter-category-input"
							aria-labelledby="aria-label-for-category-input"
							label={texts?.serviceCategoryPlaceholder || ""}
							placeholder={texts?.serviceCategoryPlaceholder || ""}
							options={serviceCatFilterOpts}
							selected={selectedServiceCategories}
							setSelected={setSelectedServiceCategories}
						/>
					</>
				)}

			</div>
			<Results
				perPage={perPage}
				queryParams={queryParams}
				errorTitle={texts?.postsErrorTitle}
				errorText={texts?.postsErrorText}
				loadMoreButtonText={texts?.loadMoreButtonText}
				renderPost={(post) => {
					return <ServiceCard key={post.id} {...post} excerpt={post.excerpt} link={post.link} icon={post.icon} />;
				}}
			/>
		</div>
	);
};

/**
 * Results
 */
interface ResultsProps {
	perPage: number;
	queryParams: object;
	errorTitle?: string;
	errorText?: string;
	loadMoreButtonText?: string;
	renderPost: (post: Post) => React.ReactNode;
}

const Results = ({
	perPage,
	queryParams,
	errorTitle = "Error loading posts.",
	errorText,
	loadMoreButtonText = "Load more",
	renderPost,
}: ResultsProps) => {
	const { data, error, isLoadingMore, canLoadMore, loadMore } = usePosts({
		perPage,
		queryParams,
	});

	if (error) {
		return <ErrorMessage title={errorTitle} text={errorText} />;
	}

	if (!data) {
		return <ResultsLoader numOfItems={perPage} />;
	}

	// Sort data by title
	const dataSorted = data.sort((a, b) => {
		const titleA = a.title.toUpperCase();
		const titleB = b.title.toUpperCase();
		if (titleA < titleB) {
		  return -1;
		}
		if (titleA > titleB) {
		  return 1;
		}
		return 0;
	});

	return (
		<div className="em-block-service-feed__results">
			<div className="em-block-service-feed__posts">
				{dataSorted.map(renderPost)}
			</div>
			<div className="em-block-service-feed__more">
				{canLoadMore && (
					<button className="em-button" onClick={loadMore}>
						{loadMoreButtonText}
					</button>
				)}

				{isLoadingMore && <Spinner />}
			</div>
		</div>
	);
};

export default App;
